import { theme } from '@maestro/local/theme'

const LOGOUT_PENDING_KEY = 'maestro:logout:pending'
const LOGOUT_PENDING_TTL_MS = 5_000

const now = () => Date.now()

/**
 * Check if a manual logout is currently pending.
 * Used to prevent race conditions between manual logout
 * and automatic session monitoring.
 */
export function isLogoutPending(): boolean {
  try {
    const value = sessionStorage.getItem(LOGOUT_PENDING_KEY)
    if (!value) {
      return false
    }

    const startedAt = Number(value)
    if (!Number.isFinite(startedAt)) {
      return false
    }

    return now() - startedAt < LOGOUT_PENDING_TTL_MS
  } catch {
    return false
  }
}

/**
 * Mark the start of a manual logout flow.
 */
export function setLogoutPending(): void {
  try {
    sessionStorage.setItem(LOGOUT_PENDING_KEY, String(now()))
  } catch {
    // SSR / privacy mode edge cases
  }
}

/**
 * Clear the logout pending flag.
 */
export function clearLogoutPending(): void {
  try {
    sessionStorage.removeItem(LOGOUT_PENDING_KEY)
  } catch {
    // SSR / privacy mode edge cases
  }
}

/**
 * Performs a safe logout redirect with race condition protection.
 * Sets a sessionStorage flag to prevent MaestroSessionProvider from
 * interpreting the session cleanup as automatic expiry.
 */
export function safeLogoutRedirect(logoutUrl?: string): void {
  const { urls } = theme()

  const redirectUrl = logoutUrl || urls.logout

  // Set timestamp flag to prevent race condition with MaestroSessionProvider
  setLogoutPending()

  // Use hard navigation to prevent race condition between manual signOut
  // and MaestroSessionProvider session monitoring (update() calls)
  window.location.assign(redirectUrl)
}
